'use client'

/**
 * Purpose: Interactive /products catalog — skin panel, filter bar, category sections, scroll-spy
 * chips, and per-card compatibility. Also the survey submit redirect target.
 * Inputs: Server-fetched `products`, compatibility maps, `hasTakenSurvey`, `skinType`/`scores`,
 * `categories` (the admin-managed list, loaded once by the server), `productPriceRange`,
 * `traits`, optional `aeoIntro`; URL search params seed the filter state.
 * Outputs: Filtered product grid; pushes filter state to the URL; passes a translated
 * `skinResultsPanel.resultsAnnouncement` to SkinResultsPanel so the scored result is announced.
 */
import './allProductsClient.css'
import type React from 'react'
import { useState, useMemo, useCallback, useRef, useEffect, useTransition } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import type { ProductItem } from '@lib/models/types'
import type { CompatibilityMatch } from '@lib/products'
import { categoryLabel, categoryPriority, categorySlugs } from '@lib/product-category-helpers'
import type { ProductCategory } from '@/generated/prisma/client'
import type { ProductIngredientItem } from '@lib/models/types'
import {
  captureCompatibilityFilterToggled,
  captureProductCategoryJump,
  captureProductFiltersOpened,
} from '@lib/interaction-telemetry'
import {
  isNavbarScrollDirectionFrozen,
  runWithNavbarScrollFrozen,
} from '@/hooks/use-navbar-scroll-hidden'
import { CategorySection } from '@components/sections/CategorySection/CategorySection'
import { ProductsStickyHeader } from '@components/headers/ProductsStickyHeader/ProductsStickyHeader'
import { ProductsFilterBar } from '@components/filters/ProductsFilterBar/ProductsFilterBar'
import { SkinResultsPanel, SKIN_TYPES } from '@components/panels/SkinResultsPanel/SkinResultsPanel'
import { SkinResultsPanelSkeleton } from '@components/panels/SkinResultsPanelSkeleton/SkinResultsPanelSkeleton'

type Props = {
  products: ProductItem[]
  compatibilityScores: Record<string, number> | null
  compatibilityDetails?: Record<string, CompatibilityMatch> | null
  hasTakenSurvey: boolean
  locale: string
  /** The full admin-managed category list — distinct from the `categories` filter-selection
   * state below, which holds the slugs currently checked in the filter bar. */
  allCategories: ProductCategory[]
  productPriceRange: { min: number; max: number }
  skinType: string
  scores: { oilyDry: number; sensitiveResistant: number; pigmented: number; wrinkled: number } | null
  traits: string[]
  aeoIntro?: React.ReactNode
}

function normalize(s: string): string {
  return s.toLowerCase().trim()
}

/** Free-text ingredient search matches either the INCI name or the localized common name. */
function ingredientMatches(ingredient: ProductIngredientItem, query: string): boolean {
  const needle = normalize(query)
  if (normalize(ingredient.inciName).includes(needle)) return true
  return ingredient.commonName ? normalize(ingredient.commonName).includes(needle) : false
}

// ─── AllProductsClient ─────────────────────────────────────────────────────────

export function AllProductsClient({
  products,
  compatibilityScores,
  compatibilityDetails,
  hasTakenSurvey,
  locale,
  allCategories,
  productPriceRange,
  skinType,
  scores,
  traits,
  aeoIntro,
}: Props) {
  const t = useTranslations('productsPage')
  const tRec = useTranslations('productRecommendation')
  const tSkin = useTranslations('skinResultsPanel')
  const categoryOptions = useMemo(
    () => categorySlugs(allCategories).map((slug) => ({ value: slug, label: categoryLabel(allCategories, slug, locale) })),
    [allCategories, locale],
  )
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [, startTransition] = useTransition()

  const compatibilityMap = useMemo(
    () => (compatibilityScores ? new Map(Object.entries(compatibilityScores)) : null),
    [compatibilityScores],
  )

  const compatibilityDetailsMap = useMemo(
    () => (compatibilityDetails ? new Map(Object.entries(compatibilityDetails)) : null),
    [compatibilityDetails],
  )

  // ── Filter state (local; URL is a side-effect) ─────────────────────────────
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const [categories, setCategories] = useState<string[]>(
    () => searchParams.getAll('category'),
  )
  const [ingredients, setIngredients] = useState<string[]>(
    () => searchParams.getAll('ingredient'),
  )
  const [sort, setSort] = useState(() => searchParams.get('sort') ?? '')
  const [excludedIngredients, setExcluded] = useState<string[]>(
    () => searchParams.getAll('exclude_ingredient'),
  )
  const [priceMin, setPriceMin] = useState(productPriceRange.min)
  const [priceMax, setPriceMax] = useState(productPriceRange.max)
  // Raw 0–4 scores, multi-select. Empty = no compatibility filter (show all).
  const [compatibilityLevels, setCompatibilityLevels] = useState<number[]>(() =>
    hasTakenSurvey
      ? searchParams
          .getAll('compatibility')
          .map(Number)
          .filter((n) => Number.isInteger(n) && n >= 0 && n <= 4)
      : [],
  )
  const [flaggedIds, setFlaggedIds] = useState<Set<string>>(new Set())
  const [filtersOpen, setFiltersOpen] = useState(false)

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── URL helpers ────────────────────────────────────────────────────────────
  function buildParams(
    overrides: Partial<{
      q: string
      categories: string[]
      ingredients: string[]
      sort: string
      excluded: string[]
      priceMax: number
      compatibility: number[]
    }>,
  ) {
    const params = new URLSearchParams(searchParams.toString())

    if ('q' in overrides) {
      if (overrides.q) params.set('q', overrides.q)
      else params.delete('q')
    }
    if ('categories' in overrides) {
      params.delete('category')
      overrides.categories!.forEach((v) => params.append('category', v))
    }
    if ('ingredients' in overrides) {
      params.delete('ingredient')
      overrides.ingredients!.forEach((v) => params.append('ingredient', v))
    }
    if ('excluded' in overrides) {
      params.delete('exclude_ingredient')
      overrides.excluded!.forEach((v) => params.append('exclude_ingredient', v))
    }
    if ('sort' in overrides) {
      if (overrides.sort) params.set('sort', overrides.sort)
      else params.delete('sort')
    }
    if ('priceMax' in overrides) {
      const val = overrides.priceMax!
      if (val < productPriceRange.max) params.set('price_max', String(val))
      else params.delete('price_max')
    }
    if ('compatibility' in overrides) {
      params.delete('compatibility')
      overrides.compatibility!.forEach((v) => params.append('compatibility', String(v)))
    }
    return params.toString()
  }

  function push(qs: string) {
    startTransition(() => router.replace(`${pathname}?${qs}`, { scroll: false }))
  }

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    if (searchDebounce.current) clearTimeout(searchDebounce.current)
    searchDebounce.current = setTimeout(
      () => push(buildParams({ q: value })),
      150,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams])

  const handleCategoryToggle = useCallback((value: string) => {
    const next = categories.includes(value) ? categories.filter((v) => v !== value) : [...categories, value]
    setCategories(next)
    push(buildParams({ categories: next }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, categories])

  const handleIngredientsChange = useCallback((tags: string[]) => {
    const deduped = excludedIngredients.filter((e) => !tags.includes(e))
    setIngredients(tags)
    setExcluded(deduped)
    push(buildParams({ ingredients: tags, excluded: deduped }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, excludedIngredients])

  const handleExcludedChange = useCallback((tags: string[]) => {
    const deduped = ingredients.filter((e) => !tags.includes(e))
    setExcluded(tags)
    setIngredients(deduped)
    push(buildParams({ ingredients: deduped, excluded: tags }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, ingredients])

  // const handleSortChange = useCallback((value: string) => {
  //   setSort(value)
  //   push(buildParams({ sort: value }))
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [pathname, searchParams])

  const handlePriceChange = useCallback((min: number, max: number) => {
    setPriceMin(min)
    setPriceMax(max)
    push(buildParams({ priceMax: max }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, productPriceRange.max])

  const handleCompatibilityToggle = useCallback((score: number) => {
    const isSelected = compatibilityLevels.includes(score)
    const next = isSelected
      ? compatibilityLevels.filter((v) => v !== score)
      : [...compatibilityLevels, score]
    setCompatibilityLevels(next)
    captureCompatibilityFilterToggled(score, !isSelected)
    push(buildParams({ compatibility: next }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams, compatibilityLevels])

  const handleClearAll = useCallback(() => {
    setSearch('')
    setCategories([])
    setIngredients([])
    setSort('')
    setExcluded([])
    setPriceMin(productPriceRange.min)
    setPriceMax(productPriceRange.max)
    setCompatibilityLevels([])
    push('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, productPriceRange])


  const handleFlagSuccess = useCallback((productId: string) => {
    setFlaggedIds((prev) => new Set([...prev, productId]))
  }, [])

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = products

    if (compatibilityLevels.length > 0 && compatibilityMap) {
      result = result.filter((p) => compatibilityLevels.includes(compatibilityMap.get(p.id) ?? 0))
    }
    if (categories.length > 0) {
      result = result.filter((p) => categories.includes(p.category ?? ''))
    }
    if (search) {
      const q = normalize(search)
      result = result.filter((p) => normalize(p.productName).includes(q))
    }
    if (ingredients.length > 0) {
      result = result.filter((p) =>
        ingredients.every((ing) => p.ingredients.some((i) => ingredientMatches(i, ing))),
      )
    }
    if (excludedIngredients.length > 0) {
      result = result.filter((p) =>
        excludedIngredients.every((exc) => !p.ingredients.some((i) => ingredientMatches(i, exc))),
      )
    }
    result = result.filter(
      (p) => p.price == null || (p.price >= priceMin && p.price <= priceMax),
    )

    result = [...result].sort((a, b) => {
      if (sort === 'price_asc') return (a.price ?? 0) - (b.price ?? 0)
      if (sort === 'price_desc') return (b.price ?? 0) - (a.price ?? 0)

      if (compatibilityMap) {
        const aScore = compatibilityMap.get(a.id) ?? -1
        const bScore = compatibilityMap.get(b.id) ?? -1
        if (aScore !== bScore) return bScore - aScore
      }

      return categoryPriority(allCategories, a.category ?? '') - categoryPriority(allCategories, b.category ?? '')
    })

    return result
  }, [products, compatibilityLevels, compatibilityMap, categories, search, ingredients, excludedIngredients, priceMin, priceMax, sort, allCategories])

  // ── Category counts (all filters except category, so counts stay live) ───────
  const filteredWithoutCategory = useMemo(() => {
    let result = products
    if (compatibilityLevels.length > 0 && compatibilityMap) {
      result = result.filter((p) => compatibilityLevels.includes(compatibilityMap.get(p.id) ?? 0))
    }
    if (search) {
      const q = normalize(search)
      result = result.filter((p) => normalize(p.productName).includes(q))
    }
    if (ingredients.length > 0) {
      result = result.filter((p) =>
        ingredients.every((ing) => p.ingredients.some((i) => ingredientMatches(i, ing))),
      )
    }
    if (excludedIngredients.length > 0) {
      result = result.filter((p) =>
        excludedIngredients.every((exc) => !p.ingredients.some((i) => ingredientMatches(i, exc))),
      )
    }
    result = result.filter(
      (p) => p.price == null || (p.price >= priceMin && p.price <= priceMax),
    )
    return result
  }, [products, compatibilityLevels, compatibilityMap, search, ingredients, excludedIngredients, priceMin, priceMax])

  const categoryCounts = useMemo(
    () =>
      Object.fromEntries(
        categorySlugs(allCategories).map((cat) => [cat, filteredWithoutCategory.filter((p) => p.category === cat).length]),
      ),
    [filteredWithoutCategory, allCategories],
  )

  // ── Products by category (from filtered list) ──────────────────────────────
  const filteredByCategory = useCallback(
    (cat: string) => filtered.filter((p) => p.category === cat),
    [filtered],
  )

  // ── Category anchors + scroll-spy ──────────────────────────────────────────
  // Chips are jump links, not filters: every category that still has products
  // renders a section, and the active chip tracks whichever is in view.
  const visibleCategories = useMemo(
    () =>
      categorySlugs(allCategories).filter((cat) =>
        filtered.some((p) => p.category === cat && !flaggedIds.has(p.id)),
      ),
    [filtered, flaggedIds, allCategories],
  )

  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map())
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const registerSection = useCallback(
    (cat: string) => (el: HTMLElement | null) => {
      if (el) sectionRefs.current.set(cat, el)
      else sectionRefs.current.delete(cat)
    },
    [],
  )

  useEffect(() => {
    // jsdom has no layout — chips still work as jump links, they just don't auto-highlight.
    if (typeof window === 'undefined') return

    let disposed = false
    let rafRetry = 0
    let thawTimer = 0

    const updateActiveFromScroll = () => {
      if (disposed) return
      const elements = visibleCategories
        .map((cat) => sectionRefs.current.get(cat))
        .filter((el): el is HTMLElement => el != null)
      if (elements.length === 0) {
        // Callback refs may not be populated on the first effect pass.
        if (rafRetry < 10) {
          rafRetry += 1
          requestAnimationFrame(updateActiveFromScroll)
        }
        return
      }

      const frozen = isNavbarScrollDirectionFrozen()
      // Chip jumps (and navbar collapse) freeze direction detection. Spy updates
      // during that window are skipped so intermediate pills don't flash — but
      // the scroll may have already settled, so schedule one catch-up tick for
      // when the freeze lifts (otherwise the chip stays on the previous category).
      if (frozen) {
        window.clearTimeout(thawTimer)
        thawTimer = window.setTimeout(() => {
          if (!disposed && !isNavbarScrollDirectionFrozen()) updateActiveFromScroll()
        }, 360)
        return
      }

      const headerEl = document.querySelector('.products-sticky-header')
      const headerRect = headerEl?.getBoundingClientRect()
      const vh = window.innerHeight
      // While the intro is on screen the sticky header is still below the fold —
      // its layout bottom can be >> vh and would zero out every section's
      // "visible below header" score. Only treat it as an inset when it's on-screen.
      const headerBottom =
        !headerRect || headerRect.top >= vh
          ? 0
          : Math.max(0, Math.min(vh, headerRect.bottom))
      // Pick the section with the most pixels visible in the content area below
      // the sticky header — that is the main product the user is looking at.
      // A fixed viewport-% IntersectionObserver band lands inside / on the
      // outgoing section while a taller mobile sticky header covers the top.
      let next: string | null = null
      let bestVisible = 0
      let bestCenterDist = Infinity
      const contentCenter = headerBottom + Math.max(0, vh - headerBottom) / 2
      for (const cat of visibleCategories) {
        const el = sectionRefs.current.get(cat)
        if (!el) continue
        const r = el.getBoundingClientRect()
        const visible = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, headerBottom))
        if (visible <= 0) continue
        const mid = (r.top + r.bottom) / 2
        const centerDist = Math.abs(mid - contentCenter)
        if (
          visible > bestVisible + 8 ||
          (Math.abs(visible - bestVisible) <= 8 && centerDist < bestCenterDist)
        ) {
          bestVisible = visible
          bestCenterDist = centerDist
          next = cat
        }
      }

      if (next) setActiveCategory(next)
    }

    updateActiveFromScroll()
    window.addEventListener('scroll', updateActiveFromScroll, { passive: true })
    window.addEventListener('resize', updateActiveFromScroll)
    const headerEl = document.querySelector('.products-sticky-header')
    const ro = headerEl ? new ResizeObserver(updateActiveFromScroll) : null
    if (headerEl && ro) ro.observe(headerEl)

    return () => {
      disposed = true
      window.clearTimeout(thawTimer)
      window.removeEventListener('scroll', updateActiveFromScroll)
      window.removeEventListener('resize', updateActiveFromScroll)
      ro?.disconnect()
    }
  }, [visibleCategories])

  const handleCategoryJump = useCallback((cat: string) => {
    setActiveCategory(cat)
    captureProductCategoryJump(cat)
    const section = sectionRefs.current.get(cat)
    if (!section) return
    // Upward chip jumps would otherwise look like scroll-up and reveal the navbar.
    // The same freeze also pauses scroll-spy updates so intermediate chips don't flash.
    runWithNavbarScrollFrozen(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [])

  // ── Skin panel (shared between desktop sidebar and mobile top) ───────────────
  const hasType = (SKIN_TYPES as readonly string[]).includes(skinType)
  const skinPanelLabels = {
    yourSkinType: tSkin('yourSkinType'),
    summaryTitle: hasType ? tSkin(`types.${skinType}.title` as Parameters<typeof tSkin>[0]) : `Skin Type ${skinType}`,
    summaryDescription: hasType ? tSkin(`types.${skinType}.description` as Parameters<typeof tSkin>[0]) : tSkin('defaultDescription'),
    yourProfileVsAverage: tSkin('yourProfileVsAverage'),
    legendYou: tSkin('legendYou'),
    legendAverage: tSkin('legendAverage'),
    axisOilyDry: tSkin('axisOilyDry'),
    axisSensitiveResistant: tSkin('axisSensitiveResistant'),
    axisPigmented: tSkin('axisPigmented'),
    axisWrinkled: tSkin('axisWrinkled'),
  }
  // /products is the survey submit redirect target, so a scored panel here is the moment the user
  // learns their result — announce it politely for screen readers (docs/accessibility.md §8).
  const skinPanel = scores && skinType ? (
    <SkinResultsPanel
      skinType={skinType}
      scores={scores}
      labels={skinPanelLabels}
      announcement={tSkin('resultsAnnouncement', { skinType })}
    />
  ) : !hasTakenSurvey ? (
    <SkinResultsPanelSkeleton locale={locale} labels={{ surveyCtaBanner: t('surveyCtaBanner'), surveyCtaButton: t('surveyCtaButton') }} />
  ) : traits.length > 0 ? (
    <p id="all-products-traits" className="all-products-traits">{traits.join(' · ')}</p>
  ) : null

  const filterBar = (
    <ProductsFilterBar
      categoryOptions={categoryOptions}
      categoryCounts={categoryCounts}
      categories={categories}
      search={search}
      ingredients={ingredients}
      excludedIngredients={excludedIngredients}
      priceMin={priceMin}
      priceMax={priceMax}
      productPriceRange={productPriceRange}
      compatibilityOptions={[4, 3, 2, 1, 0].map((score) => ({
        score,
        label: t('compatibilityPercent', { percent: score * 25 }),
      }))}
      selectedCompatibility={compatibilityLevels}
      showCompatibility={hasTakenSurvey}
      onCategoryToggle={handleCategoryToggle}
      onSearchChange={handleSearchChange}
      onIngredientsChange={handleIngredientsChange}
      onExcludedIngredientsChange={handleExcludedChange}
      onPriceChange={handlePriceChange}
      onCompatibilityToggle={handleCompatibilityToggle}
      onClearAll={handleClearAll}
      locale={locale}
    />
  )

  return (
    <main id="all-products-client" className="all-products-page">
      <div id="all-products-intro" className="all-products-container">
        {skinPanel && <div id="all-products-skin-panel" className="all-products-skin-panel">{skinPanel}</div>}

        {aeoIntro}
      </div>

      {/* Full-bleed sticky shell so its sand bg covers edge-to-edge cards scrolling underneath. */}
      <ProductsStickyHeader
        title={tRec('pageTitle')}
        subtitle={t('subtitle')}
        categories={visibleCategories.map((cat) => ({
          value: cat,
          label: categoryLabel(allCategories, cat, locale),
        }))}
        activeCategory={activeCategory}
        filtersLabel={t('filtersButton')}
        jumpLabel={(label) => t('jumpToCategory', { category: label })}
        onSelectCategory={handleCategoryJump}
        onOpenFilters={() => {
          captureProductFiltersOpened()
          setFiltersOpen(true)
        }}
      />

      {filtersOpen && (
        <>
          <div id="products-filter-panel-overlay" className="filter-panel-overlay" onClick={() => setFiltersOpen(false)} />
          <div id="products-filter-panel" className="filter-panel">
            <div id="products-filter-close-row" className="all-products-filter-close-row">
              <button
                id="products-filter-panel-close"
                onClick={() => setFiltersOpen(false)}
                aria-label={t('filtersClose')}
                className="filter-panel-close"
              >
                ✕
              </button>
            </div>
            {filterBar}
          </div>
        </>
      )}

      {/* Category strips sit outside the max-width container so cards are
          viewport-edge flush on every breakpoint. */}
      {filtered.length === 0 ? (
        <p id="all-products-no-results" className="all-products-no-results all-products-container">{t('columnNoResults')}</p>
      ) : (
        <div id="all-products-categories" className="all-products-categories">
          {visibleCategories.map((cat) => (
            <CategorySection
              key={cat}
              category={cat}
              label={categoryLabel(allCategories, cat, locale)}
              products={filteredByCategory(cat)}
              flaggedIds={flaggedIds}
              compatibilityMap={compatibilityMap}
              compatibilityDetailsMap={compatibilityDetailsMap}
              onFlagSuccessAction={handleFlagSuccess}
              sectionRefAction={registerSection(cat)}
            />
          ))}
        </div>
      )}
    </main>
  )
}
